import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { styled } from "@mui/material/styles";
import useLayerApi from "../api/useLayerApi";
import { useSession } from "../context/SessionContext";
import { userHasAccess } from "../services/RoleBasedAccess";
import LayersTable from "../components/layers/LayersTable";
import { Layer } from "../state/types";

const ALLOWED_ROLES = ["Admin", "Support"];

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

type Severity = "success" | "error" | "warning" | "info";

interface Notification {
  open: boolean;
  message: string;
  severity: Severity;
}

const Layers: React.FC = () => {
  const theme = useTheme();
  const { user } = useSession();
  const { createLayerApi, uploadLayerFileApi, listLayersApi } = useLayerApi();

  const [layers, setLayers] = useState<Layer[]>([]);
  const [layersLoading, setLayersLoading] = useState(false);
  const [layersError, setLayersError] = useState<string | null>(null);

  const [category, setCategory] = useState("");
  const [group, setGroup] = useState("");
  const [name, setName] = useState("");
  const [folder, setFolder] = useState("");
  const [description, setDescription] = useState("");
  const [file, setFile] = useState<File | null>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadPct, setUploadPct] = useState(0);
  const [notification, setNotification] = useState<Notification>({
    open: false,
    message: "",
    severity: "info",
  });

  const hasAccess = userHasAccess(user?.role, ALLOWED_ROLES);

  const fetchLayers = useCallback(async () => {
    setLayersLoading(true);
    setLayersError(null);
    try {
      const result = await listLayersApi();
      setLayers(result);
    } catch (err: any) {
      setLayersError(err?.message || "Failed to load layers.");
    } finally {
      setLayersLoading(false);
    }
  }, [listLayersApi]);

  useEffect(() => {
    if (hasAccess) {
      fetchLayers();
    }
  }, [hasAccess, fetchLayers]);

  const fileName = file?.name ?? "";
  const contentType = file?.type || "image/png";
  const skPreview = `${group}#${name}`;
  const objectKeyPreview = folder ? `${folder}/${fileName}` : fileName;

  const closeNotification = () => setNotification((prev) => ({ ...prev, open: false }));

  const resetForm = () => {
    setCategory("");
    setGroup("");
    setName("");
    setFolder("");
    setDescription("");
    setFile(null);
    setUploadPct(0);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selected = event.target.files?.[0] ?? null;
    setFile(selected);
  };

  const handleSubmit = async () => {
    if (!category.trim() || !group.trim() || !name.trim() || !file) {
      setNotification({
        open: true,
        message: "Category, group, name, and an image file are all required.",
        severity: "warning",
      });
      return;
    }

    setIsSubmitting(true);
    setUploadPct(0);

    const layerContentType = file.type || "image/png";

    try {
      const res = await createLayerApi({
        category,
        group,
        name,
        fileName: file.name,
        folder,
        description,
        contentType: layerContentType,
      });

      await uploadLayerFileApi(res.presignedUrl, file, layerContentType, setUploadPct);

      setNotification({
        open: true,
        message: "Layer created and uploaded successfully.",
        severity: "success",
      });
      resetForm();
      fetchLayers();
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 400) {
        setNotification({ open: true, message: "Bad request — incomplete data.", severity: "error" });
      } else if (status === 409) {
        setNotification({
          open: true,
          message: "A layer with that category + group + name already exists.",
          severity: "warning",
        });
      } else if (status === 500) {
        setNotification({ open: true, message: "Internal server error.", severity: "error" });
      } else {
        setNotification({
          open: true,
          message: err?.message || "Something went wrong creating the layer.",
          severity: "error",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!hasAccess) {
    return (
      <Box sx={{ display: "grid", placeItems: "center", height: "100%", p: 4 }}>
        <Alert severity="error" data-cy="layer-access-denied">
          You do not have permission to view this page.
        </Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: "100%",
        overflow: "auto",
        p: { xs: 2, md: 3 },
        backgroundColor: theme.palette.background.default,
      }}
    >
      <Stack spacing={2} sx={{ maxWidth: 1100, mx: "auto" }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.25, md: 2.75 },
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.4 }}>
            Layers
          </Typography>
          <Typography variant="h4" sx={{ fontWeight: 700, mt: 0.5 }}>
            Layers
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
            Browse existing layers in the bw-layers table, or register a new one below.
          </Typography>
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.25, md: 2.75 },
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <LayersTable
            layers={layers}
            loading={layersLoading}
            error={layersError}
            onRefresh={fetchLayers}
          />
        </Paper>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.25, md: 2.75 },
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
          }}
        >
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
            Create Layer
          </Typography>
          <Stack spacing={2.25} sx={{ maxWidth: 640 }}>
            <TextField
              label="Category"
              required
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              fullWidth
              inputProps={{ "data-cy": "layer-category-input" }}
            />
            <TextField
              label="Group"
              required
              value={group}
              onChange={(e) => setGroup(e.target.value)}
              fullWidth
              inputProps={{ "data-cy": "layer-group-input" }}
            />
            <TextField
              label="Name"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              inputProps={{ "data-cy": "layer-name-input" }}
            />
            <TextField
              label="Folder"
              value={folder}
              onChange={(e) => setFolder(e.target.value)}
              helperText="Leave blank for bucket root"
              fullWidth
              inputProps={{ "data-cy": "layer-folder-input" }}
            />
            <TextField
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              multiline
              minRows={2}
              fullWidth
              inputProps={{ "data-cy": "layer-description-input" }}
            />

            <Box>
              <Button
                component="label"
                variant="outlined"
                startIcon={<CloudUploadIcon />}
                data-cy="layer-file-btn"
              >
                {file ? "Change Image" : "Select Image"}
                <VisuallyHiddenInput type="file" accept="image/*" onChange={handleFileChange} />
              </Button>
              {file ? (
                <Chip
                  label={fileName}
                  size="small"
                  onDelete={() => setFile(null)}
                  sx={{ ml: 1.5 }}
                  data-cy="layer-file-chip"
                />
              ) : (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 1.5 }}>
                  No file selected
                </Typography>
              )}
            </Box>

            <Paper variant="outlined" sx={{ p: 1.75, borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary">
                Preview
              </Typography>
              <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                <Typography variant="body2">
                  <strong>SK:</strong> {skPreview}
                </Typography>
                <Typography variant="body2">
                  <strong>Object key:</strong> {objectKeyPreview || "--"}
                </Typography>
                <Typography variant="body2">
                  <strong>Content type:</strong> {contentType}
                </Typography>
              </Stack>
            </Paper>

            {isSubmitting && uploadPct > 0 ? (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Uploading… {uploadPct}%
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={uploadPct}
                  sx={{ height: 8, borderRadius: 999, mt: 0.5 }}
                />
              </Box>
            ) : null}

            <Button
              variant="contained"
              size="large"
              onClick={handleSubmit}
              disabled={isSubmitting}
              startIcon={<CloudUploadIcon />}
              data-cy="layer-submit-btn"
            >
              {isSubmitting ? "Creating…" : "Create Layer"}
            </Button>
          </Stack>
        </Paper>
      </Stack>

      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={closeNotification}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert onClose={closeNotification} severity={notification.severity} sx={{ width: "100%" }}>
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Layers;
